import type { Slide } from '../types'

type Props = {
  currentSlide: Slide | undefined
  currentIndex: number
  slides: Slide[]
  onPrev: () => void
  onNext: () => void
  onSelectSlide: (index: number) => void
}

const HomeFallbackHero = ({
  currentSlide,
  currentIndex,
  slides,
  onPrev,
  onNext,
  onSelectSlide,
}: Props) => {
  if (!currentSlide) return null

  const hasSlideImage = Boolean(currentSlide.image)

  return (
    <section className="relative left-1/2 isolate min-h-[520px] w-screen -translate-x-1/2 overflow-hidden bg-slate-950 sm:min-h-[560px] lg:min-h-[620px]">
      {hasSlideImage ? (
        <img
          src={currentSlide.image}
          alt={currentSlide.title}
          loading="eager"
          decoding="async"
          fetchPriority="high"
          sizes="100vw"
          className="absolute inset-0 h-full w-full scale-[1.02] object-cover object-[64%_center] lg:object-[72%_center]"
        />
      ) : (
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-[radial-gradient(circle_at_18%_18%,rgba(14,165,233,0.34),transparent_34%),radial-gradient(circle_at_82%_8%,rgba(16,185,129,0.18),transparent_30%),linear-gradient(135deg,#020617_0%,#0f172a_50%,#111827_100%)]"
        />
      )}

      {hasSlideImage ? (
        <>
          <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(2,6,23,0.96)_0%,rgba(2,6,23,0.88)_32%,rgba(2,6,23,0.46)_56%,rgba(2,6,23,0.12)_76%,rgba(2,6,23,0.02)_100%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(2,6,23,0.26)_0%,rgba(2,6,23,0.04)_34%,rgba(2,6,23,0.18)_70%,rgba(2,6,23,0.72)_100%)]" />
          <div className="absolute inset-y-0 right-0 hidden w-[56%] bg-[radial-gradient(circle_at_62%_44%,rgba(255,255,255,0.18),transparent_36%),linear-gradient(90deg,rgba(2,6,23,0.18),rgba(2,6,23,0)_38%)] lg:block" />
        </>
      ) : (
        <>
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/94 via-slate-950/68 to-slate-950/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/86 via-slate-950/16 to-slate-950/24" />
        </>
      )}

      <div className="relative flex min-h-[520px] flex-col px-6 py-6 sm:min-h-[560px] sm:px-10 sm:py-8 lg:min-h-[620px] lg:px-28">
        <div className="flex flex-1 items-center pb-16 pt-16 sm:pb-20 lg:pt-20">
          <div className="max-w-3xl space-y-5 [text-shadow:0_2px_22px_rgba(2,6,23,0.62)]">
            <h2 className="text-4xl font-black leading-[1.02] text-white sm:text-5xl lg:text-7xl">
              {currentSlide.title}
            </h2>
            <p className="max-w-2xl text-base leading-relaxed text-white/78 sm:text-lg lg:text-xl">
              {currentSlide.description}
            </p>

            <div className="flex flex-wrap items-center gap-3 pt-2">
              {currentSlide.ctaLabel && (
                <a
                  href={currentSlide.ctaHref || '#'}
                  className="inline-flex min-h-12 items-center justify-center rounded-lg px-5 text-sm font-semibold text-white shadow-xl shadow-black/20 transition hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/55"
                  style={{ background: 'var(--theme-color-primary, #047857)' }}
                >
                  {currentSlide.ctaLabel}
                </a>
              )}
              <a
                href="/search"
                className="inline-flex min-h-12 items-center justify-center rounded-lg border border-white/24 bg-white/12 px-5 text-sm font-semibold text-white backdrop-blur transition hover:-translate-y-0.5 hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/55"
              >
                Sfoglia catalogo
              </a>
            </div>
          </div>
        </div>

        <div className="pointer-events-none absolute inset-y-0 left-0 right-0 flex items-center justify-between">
          <button
            onClick={onPrev}
            className="pointer-events-auto group grid h-16 w-10 place-items-center text-white/58 transition hover:text-white/88 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 sm:w-12 lg:w-14"
            aria-label="Slide precedente"
          >
            <svg
              viewBox="0 0 24 48"
              className="h-9 w-4.5 fill-none stroke-current stroke-[1.7] drop-shadow-[0_2px_10px_rgba(2,6,23,0.65)] transition group-hover:-translate-x-0.5"
              aria-hidden
            >
              <path d="M17 8 7 24l10 16" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          <button
            onClick={onNext}
            className="pointer-events-auto group grid h-16 w-10 place-items-center text-white/58 transition hover:text-white/88 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 sm:w-12 lg:w-14"
            aria-label="Slide successiva"
          >
            <svg
              viewBox="0 0 24 48"
              className="h-9 w-4.5 fill-none stroke-current stroke-[1.7] drop-shadow-[0_2px_10px_rgba(2,6,23,0.65)] transition group-hover:translate-x-0.5"
              aria-hidden
            >
              <path d="m7 8 10 16L7 40" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        <div className="absolute inset-x-0 bottom-6 flex justify-center px-5">
          <div className="flex items-center gap-2">
            {slides.map((slide, idx) => (
              <button
                key={slide.id}
                onClick={() => onSelectSlide(idx)}
                className="grid h-6 w-6 place-items-center rounded-full transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
                aria-label={`Vai alla slide ${idx + 1}`}
                aria-current={idx === currentIndex ? 'true' : undefined}
              >
                <span
                  className={`h-2 rounded-full transition-all ${
                    idx === currentIndex ? 'w-7' : 'w-2 bg-white/48 hover:bg-white/72'
                  }`}
                  style={
                    idx === currentIndex
                      ? { background: 'var(--theme-color-primary, #10b981)' }
                      : undefined
                  }
                />
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export default HomeFallbackHero
